import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import './MediaForm.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';

function MediaForm({ openTab, closeTab, activeTab, mediaId }) {
  const isEditMode = !!mediaId;

  // Form state
  const [name, setName] = useState('');
  const [mid, setMid] = useState('');
  const [type, setType] = useState('');
  const [isAvailable, setIsAvailable] = useState(1);
  const [note, setNote] = useState('');

  // Publisher autocomplete state
  const [publisherSearch, setPublisherSearch] = useState('');
  const [publisherResults, setPublisherResults] = useState([]);
  const [publisherLoading, setPublisherLoading] = useState(false);
  const [showPublisherDropdown, setShowPublisherDropdown] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const publisherRef = useRef(null);
  const dropdownRef = useRef(null);

  // Media types from lookup table
  const [mediaTypes, setMediaTypes] = useState([]);

  // Form submission state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Load media types from lookup table
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const result = await api.getMediaTypes();
        setMediaTypes(result.data || []);
      } catch (err) {
        console.error('Failed to load media types:', err);
      }
    };
    fetchTypes();
  }, []);

  // Load existing medium data in edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchMedium = async () => {
      setLoadingData(true);
      try {
        const result = await api.getMedium(mediaId);
        const data = result.data;
        if (data) {
          setName(data.name || '');
          setMid(data.mid || '');
          setType(data.type || '');
          setIsAvailable(data.is_available ? 1 : 0);
          setNote(data.note || '');

          // Set publisher if exists
          if (data.publisher_company_id) {
            setSelectedPublisher({
              id: data.publisher_company_id,
              company_name: data.publisher_name || '',
              ico: null, // Will be loaded if user clicks
            });
            setPublisherSearch(data.publisher_name || '');

            // Try to load full company data for ICO
            try {
              const companyResult = await api.getCompany(data.publisher_company_id);
              if (companyResult.data) {
                setSelectedPublisher({
                  id: data.publisher_company_id,
                  company_name: companyResult.data.company_name || data.publisher_name || '',
                  ico: companyResult.data.ico || null,
                });
              }
            } catch (e) {
              // Ignore - we still have the basic info
            }
          }
          setInitialLoaded(true);
        }
      } catch (err) {
        setError('Nepodařilo se načíst data média.');
        console.error('Failed to load medium:', err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchMedium();
  }, [mediaId, isEditMode]);

  // Track form changes (only after initial load in edit mode)
  useEffect(() => {
    if (isEditMode && !initialLoaded) return;
    if (name || mid || type || note || selectedPublisher) {
      setIsDirty(true);
    }
  }, [name, mid, type, note, selectedPublisher, isEditMode, initialLoaded]);

  // Publisher search debounce
  useEffect(() => {
    // Don't search if a publisher is already selected
    if (selectedPublisher) {
      setShowPublisherDropdown(false);
      return;
    }

    if (!publisherSearch || publisherSearch.length < 2) {
      setPublisherResults([]);
      setShowPublisherDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setPublisherLoading(true);
      try {
        const result = await api.searchCompanies(publisherSearch);
        const companies = result.data || [];
        setPublisherResults(companies);
        setShowPublisherDropdown(companies.length > 0);
      } catch (err) {
        console.error('Publisher search failed:', err);
        setPublisherResults([]);
      } finally {
        setPublisherLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [publisherSearch, selectedPublisher]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        publisherRef.current && !publisherRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setShowPublisherDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPublisher = (company) => {
    setSelectedPublisher(company);
    setPublisherSearch(company.company_name);
    setShowPublisherDropdown(false);
  };

  const handleClearPublisher = () => {
    setSelectedPublisher(null);
    setPublisherSearch('');
    setPublisherResults([]);
  };

  const handlePublisherInputChange = (e) => {
    const val = e.target.value;
    setPublisherSearch(val);
    // If user edits the text after selecting, clear the selection
    if (selectedPublisher && val !== selectedPublisher.company_name) {
      setSelectedPublisher(null);
    }
  };

  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm('Máte neuložené změny. Opravdu chcete zavřít formulář?');
      if (!confirmed) return;
    }
    if (closeTab && activeTab) {
      closeTab(activeTab);
    }
  }, [isDirty, closeTab, activeTab]);

  const handleSave = async () => {
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Název titulu je povinný.');
      return;
    }
    if (!type) {
      setError('Typ média je povinný.');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        mid: mid.trim() || null,
        type,
        is_available: isAvailable,
        publisher_company_id: selectedPublisher ? selectedPublisher.id : null,
        publisher_name: selectedPublisher ? selectedPublisher.company_name : null,
        note: note.trim() || null,
      };

      if (isEditMode) {
        // Update existing medium
        await api.updateMedium(mediaId, data);

        // Close this edit tab
        if (closeTab && activeTab) {
          closeTab(activeTab);
        }
        // Open/refresh the detail of the updated medium
        openTab(
          `media-detail-${mediaId}`,
          name.trim(),
          `/media-pricelist/${mediaId}`,
          'MediaPricelistDetail',
          { mediaId: mediaId }
        );
        showToast('Změny byly úspěšně uloženy.', 'success');
        window.dispatchEvent(new CustomEvent('media:refresh', { detail: { mediaId } }));
      } else {
        // Create new medium
        const result = await api.createMedium(data);
        const newId = result.data?.id;

        if (newId) {
          // Close this form tab
          if (closeTab && activeTab) {
            closeTab(activeTab);
          }
          // Open the detail of the newly created medium
          openTab(
            `media-detail-${newId}`,
            name.trim(),
            `/media-pricelist/${newId}`,
            'MediaPricelistDetail',
            { mediaId: newId }
          );
          showToast('Médium bylo úspěšně vytvořeno.', 'success');
        }
      }
    } catch (err) {
      setError(err.message || `Nepodařilo se ${isEditMode ? 'uložit změny' : 'vytvořit médium'}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToMediaList = () => {
    openTab('media-pricelist', 'Média a ceníky', '/media-pricelist', 'MediaPricelist');
  };

  if (loadingData) {
    return (
      <Container fluid className="media-form-container">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Načítání dat…</div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="media-form-container">

      {/* Breadcrumb */}
      <div className="breadcrumb-custom">
        <a onClick={handleGoToMediaList}>Média a ceníky</a>
        <i className="bi bi-chevron-right" style={{ color: '#6c757d', fontSize: '.85rem' }}></i>
        <span className="fw-semibold">{isEditMode ? 'Upravit médium' : 'Nové médium'}</span>
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {/* Form Card */}
      <div className="media-form-card">
        <div className="card-header-custom">
          <h5 className="mb-0">{isEditMode ? 'Upravit médium' : 'Nové médium'}</h5>
        </div>

        <div className="card-body-custom">

          {/* Základní informace */}
          <div className="form-section">
            <div className="section-title">Základní informace</div>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Název titulu <span className="text-danger">*</span></label>
                <Form.Control
                  type="text"
                  placeholder="např. Noviny kraje 12"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Interní kód</label>
                <Form.Control
                  type="text"
                  placeholder="např. T12"
                  value={mid}
                  onChange={(e) => setMid(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Typ média <span className="text-danger">*</span></label>
                <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">— vyberte —</option>
                  {mediaTypes.map(mt => (
                    <option key={mt.id} value={mt.id}>{mt.label}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Stav</label>
                <Form.Select
                  value={isAvailable}
                  onChange={(e) => setIsAvailable(parseInt(e.target.value))}
                >
                  <option value={1}>Aktivní</option>
                  <option value={0}>Neaktivní</option>
                </Form.Select>
              </div>
            </div>
          </div>

          {/* Vydavatel */}
          <div className="form-section">
            <div className="section-title">Vydavatel</div>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Firma (CRM)</label>
                <div className="publisher-autocomplete-wrapper">
                  <div className="input-group">
                    <Form.Control
                      ref={publisherRef}
                      type="text"
                      placeholder="Začněte psát název firmy nebo IČO…"
                      value={publisherSearch}
                      onChange={handlePublisherInputChange}
                      onFocus={() => {
                        if (publisherResults.length > 0 && !selectedPublisher) {
                          setShowPublisherDropdown(true);
                        }
                      }}
                    />
                    {selectedPublisher ? (
                      <Button variant="outline-secondary" onClick={handleClearPublisher}>
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    ) : (
                      <Button variant="outline-secondary" disabled>
                        <i className="bi bi-search"></i>
                      </Button>
                    )}
                  </div>

                  {/* Autocomplete dropdown */}
                  {showPublisherDropdown && (
                    <div className="autocomplete-dropdown" ref={dropdownRef}>
                      {publisherLoading ? (
                        <div className="autocomplete-loading">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Hledám…
                        </div>
                      ) : publisherResults.length === 0 ? (
                        <div className="autocomplete-no-results">Žádné výsledky</div>
                      ) : (
                        publisherResults.map(company => (
                          <div
                            key={company.id}
                            className="autocomplete-item"
                            onClick={() => handleSelectPublisher(company)}
                          >
                            <div className="company-name">{company.company_name}</div>
                            {company.ico && (
                              <div className="company-ico">IČO: {company.ico}</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="form-hint">Propojení s CRM záznamem vydavatele</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">IČO</label>
                <Form.Control
                  type="text"
                  placeholder="—"
                  value={selectedPublisher?.ico || ''}
                  readOnly
                />
                <div className="form-hint">Načte se z CRM</div>
              </div>
            </div>
          </div>

          {/* Poznámka */}
          <div className="form-section">
            <div className="section-title">Poznámka</div>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Interní poznámka k médiu (volitelné)…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="form-footer">
        <div className="form-footer-hint">
          <span className="text-danger">*</span> Povinná pole
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleCancel}>
            Zrušit
          </Button>
          <Button variant="primary" className="px-4" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Ukládám…
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i>
                {isEditMode ? 'Uložit změny' : 'Uložit médium'}
              </>
            )}
          </Button>
        </div>
      </div>

    </Container>
  );
}

export default MediaForm;
